// @givmo/board — a content fingerprint, shown whole: selectable, with a copy button.
//
// One component for every place a reader may need to note a fingerprint down or compare it with
// another copy: the machine-readable copy's dialog on the Minute book and a record's own page
// (CBG-UX2); since CBG-UX4 also a consent and each signature on it, the minutes, a legal hold's
// report, an import batch, and the director's consent and record pages (the isolated director
// client mounts it too — it makes no request). WHOLE, never shortened — a shortened fingerprint
// cannot be compared against anything, and noting it down is why it is on screen. The copy button
// sits at the block's edge; "Copied" confirms it beside the button.

import { useEffect, useState } from 'react';
import { Icon, IconButton, toast } from '@givmo/design-system';
import { Check, Copy } from 'lucide-react';

/** How long "Copied" stays beside the copy button. */
const COPIED_FOR_MS = 2000;

/**
 * The fingerprint in its block. `testId` names the value; the copy button and its confirmation are
 * `${testId}-copy` and `${testId}-copied`.
 *
 * `label` says whose fingerprint it is, for a page that shows several (a list's rows, a consent's
 * acts): the copy button is then named "Copy the fingerprint of <label>", so a screen reader moving
 * from button to button hears which one it is on. Nothing visible changes — the name is the
 * button's accessible name only. Without it, the one fingerprint on a page keeps the plain name.
 */
export function Fingerprint({
  value,
  testId,
  label,
}: {
  value: string;
  testId: string;
  label?: string | null;
}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), COPIED_FOR_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard access can be refused (permissions, an insecure origin). The fingerprint is on
      // screen and selectable, so this is a missing convenience, not a failure worth an error.
      toast.error('We couldn’t copy it — you can select the fingerprint and copy it yourself.');
    }
  }

  return (
    <div className="flex items-start gap-8 rounded-md border border-border-subtle bg-surface-sunken py-6 pl-12 pr-6">
      <code
        className="min-w-0 flex-1 select-all break-all py-4 font-mono text-s13 leading-h15 text-text-strong"
        data-testid={testId}
      >
        {value}
      </code>
      <span className="flex shrink-0 items-center gap-4">
        {copied ? (
          <span
            role="status"
            className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary"
            data-testid={`${testId}-copied`}
          >
            Copied
          </span>
        ) : null}
        <IconButton
          aria-label={
            label && label.trim()
              ? `Copy the fingerprint of ${label.trim()}`
              : 'Copy the fingerprint'
          }
          tone="ghost"
          size="sm"
          onClick={() => void copy()}
          testId={`${testId}-copy`}
        >
          <Icon icon={copied ? Check : Copy} size={16} aria-hidden />
        </IconButton>
      </span>
    </div>
  );
}
